import React, { useState } from 'react';
import {
  CategoryTreeItem,
  FilterRoute,
  OrderLine,
  ProductCardVariant,
  ProductVariant,
  UOM,
} from '@zonofi/zono-money-store';
import { ProductCard, ProductCardProps } from '../product-card/product-card';
import { isEmpty } from 'lodash';
import {
  EmptyStateView,
  EndListView,
  FilterModal,
  ListSkeleton,
  PullRefreshContainer,
  SpinnerLoader,
} from '../../components';
import { SVGS } from '../../assets';
import './product-list.scss';
import { ProductFilters } from '../product-filters';
import { ChipFilterSection } from '../../types';
import { SelectUOMDrawer } from '../../order';
import { mapProductVariantToOrderLine } from '../helper';

interface ProductListProps extends Omit<ProductCardProps, 'product'> {
  data: ProductVariant[];
  onScroll: () => void;
  lastItemRef: React.MutableRefObject<HTMLDivElement | null>;
  total: number;
  searchKey: string;
  isLoading: boolean;
  setShowFilter: React.Dispatch<React.SetStateAction<boolean>>;
  showFilter: boolean;
  filterIndex?: number;
  setFilterIndex?: (val: number) => void;
  isEndOfList?: boolean;
  segments?: ChipFilterSection[];
  categories?: CategoryTreeItem;
  updateSelectedCategories?: React.Dispatch<
    React.SetStateAction<CategoryTreeItem[]>
  >;
  updateSelectedSegments?: React.Dispatch<React.SetStateAction<string>>;
  isByCustomer?: boolean;
  onRefresh?: () => void | Promise<void>;
}

export const ProductList: React.FC<ProductListProps> = ({
  data,
  onScroll,
  lastItemRef,
  total,
  searchKey,
  variant,
  isLoading,
  setShowFilter,
  showFilter,
  selectedProducts,
  isEndOfList,
  segments: segmentFilters = [] as ChipFilterSection[],
  categories: categoryTree = {} as CategoryTreeItem,
  updateSelectedCategories: setSelectedCategories = () => {},
  updateSelectedSegments: setSelectedSegment = () => {},
  onRefresh,
  ...props
}) => {
  const [uomModalState, setUomModalState] = useState<{
    open: boolean;
    line?: OrderLine;
  }>({
    open: false,
    line: undefined,
  });

  const onCloseUomModal = () => {
    setUomModalState({ open: false, line: undefined });
  };

  const onOpenUomModal = (product: ProductVariant) => {
    const line = mapProductVariantToOrderLine(product);
    setUomModalState({ open: true, line });
  };

  const onUomChange = (uom: UOM) => {
    const lineItem = uomModalState.line;
    props.onAddProductPress?.(
      lineItem?.productVariant as unknown as ProductVariant,
      uom
    );

    onCloseUomModal();
  };

  return (
    <>
      <div className="product-list">
        <ProductFilters
          segments={segmentFilters}
          categories={categoryTree as CategoryTreeItem}
          updateSelectedCategories={setSelectedCategories}
          updateSelectedSegments={setSelectedSegment}
        />
        <PullRefreshContainer onScroll={onScroll} onRefresh={onRefresh}>
          {isLoading ? (
            <ListSkeleton />
          ) : (
            <>
              {data.length > 0 && searchKey?.length > 0 && total > 0 && (
                <span className="product-list-total-text font-semibold">{`${total} Products Found`}</span>
              )}
              {isEmpty(data) ? (
                <EmptyStateView
                  imageSrc={SVGS.NoProductIcon}
                  message="No Products found"
                />
              ) : (
                <>
                  {!isEmpty(data) &&
                    variant === ProductCardVariant.EDIT_PRICE && (
                      <div className="product-card-header">
                        <span className="font-semibold">{'Products'}</span>
                        <span className="price-text-gap font-semibold">
                          {'Price'}
                        </span>
                      </div>
                    )}

                  {data?.map((product) => {
                    return (
                      <ProductCard
                        key={product.id}
                        variant={variant}
                        product={product}
                        selectedProducts={selectedProducts}
                        openUomModal={() => onOpenUomModal(product)}
                        {...props}
                      />
                    );
                  })}
                  <div ref={lastItemRef} />
                  {!isEndOfList && <SpinnerLoader />}
                  {isEndOfList && <EndListView />}
                </>
              )}
            </>
          )}
        </PullRefreshContainer>
      </div>
      <FilterModal
        route={FilterRoute.Product}
        onClose={() => setShowFilter(false)}
        open={showFilter}
      />
      <SelectUOMDrawer
        open={uomModalState.open}
        onClose={onCloseUomModal}
        onUomChange={onUomChange}
        currentLine={uomModalState.line}
      />
    </>
  );
};
